// SSR-safe localStorage utility
// Provides safe access to localStorage that won't break during SSR or in non-browser environments

const isBrowser =
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

/**
 * SSR-safe localStorage wrapper
 */
export const storage = {
  /**
   * Get an item from localStorage
   */
  getItem(key: string): string | null {
    if (!isBrowser) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  /**
   * Set an item in localStorage.
   *
   * Returns `true` when the value was persisted, `false` when it was not. BACKLOG PB-05: this
   * previously swallowed the failure and returned `void`, so a write that hit the ~5 MB quota
   * looked identical to one that succeeded — every caller believed it had saved. That is the
   * failure mode that costs the most to diagnose, and it is why an AFE appeared to save and did
   * not (PB-01). The return value is additive: existing callers that ignore it behave exactly as
   * before, but they can no longer claim they were not told.
   */
  setItem(key: string, value: string): boolean {
    if (!isBrowser) return false;
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      // Quota exceeded / storage disabled. Report it — silence here is the defect.
      // eslint-disable-next-line no-console
      console.error(
        `storage.setItem("${key}") failed — ${value.length} bytes not saved.`,
        e,
      );
      return false;
    }
  },

  /**
   * Remove an item from localStorage
   */
  removeItem(key: string): void {
    if (!isBrowser) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  },

  /**
   * Clear all localStorage
   */
  clear(): void {
    if (!isBrowser) return;
    try {
      localStorage.clear();
    } catch {
      // Ignore errors
    }
  },
};

/**
 * Check if running in browser environment
 */
export const isClient = isBrowser;

/**
 * Safe window access for event listeners
 */
export const safeWindow = {
  addEventListener: (
    event: string,
    handler: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void => {
    if (!isBrowser) return;
    window.addEventListener(event, handler, options);
  },

  removeEventListener: (
    event: string,
    handler: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void => {
    if (!isBrowser) return;
    window.removeEventListener(event, handler, options);
  },
};
